import * as React from 'react';

import './AuthorizationError.scss';

import { removeTypenameFromObject } from 'common/apollo/helpers';
import EmptyView from 'common/components/core/EmptyView/EmptyView';
import LocalTable from 'common/components/core/Table/LocalTable';
import LogoutButton from 'common/components/LogoutButton/LogoutButton';
import { getConfig } from 'common/helpers/configHelper';
import Icons from 'common/icons';

type MyNutanixAssertions = {
  roles: { name: string }[];
};

/**
 * The AuthorizationError component.
 *
 * A view displayed whenever there is an authorization error in the app.
 */
export interface AuthorizationErrorProps {
  assertions: string;
  identityProvider: string;
}

const tableStructure = {
  columnWidths: {
    key: '25%',
    value: '75%',
  },
};

const columnDefinitions = [
  {
    key: 'key',
    title: 'Field',
  },
  {
    key: 'value',
    title: 'Value',
  },
];

const AuthorizationError: React.FC<AuthorizationErrorProps> = ({ assertions, identityProvider }) => {
  const getMyNutanixRedirectUrl = () => {
    const assertionsEntries = removeTypenameFromObject(JSON.parse(assertions)) as MyNutanixAssertions;
    const { roles } = assertionsEntries;
    const hasFrameOrClusters = roles?.some(role => ['frame-admin', 'clusters-admin'].includes(role.name));
    const { myNutanixUrl } = getConfig();

    // If user has Frame/Clusters role there is an internal error. Show the error view.
    if (hasFrameOrClusters) {
      return undefined;
    }

    return { url: myNutanixUrl, label: 'Go to My Nutanix' };
  };

  const getAssertions = () => {
    const assertionsEntries = removeTypenameFromObject(JSON.parse(assertions));
    return Object.entries(assertionsEntries).map(entry => {
      return { key: entry[0], value: JSON.stringify(entry[1]) };
    });
  };

  const myNutanixRedirectData = identityProvider === 'mynutanix-oauth2' ? getMyNutanixRedirectUrl() : undefined;

  const baseClass = 'authorization-error';
  return (
    <EmptyView
      iconUrl={Icons.USERS}
      description={
        <div>
          <span className={`${baseClass}__error-title mb-m`}>Unauthorized</span>
          <br />
          You've authenticated successfully, but we couldn't find any matching permissions for you.
          <br />
          Please contact your administrator for assistance.
        </div>
      }
      className={baseClass}
    >
      {assertions && assertions !== '{}' && (
        <div className="text-s pl-m pr-m mb-l">
          <label>Received SAML Assertions / Claims</label>
          <div className={`${baseClass}__assertions--table-holder`}>
            <LocalTable dataSource={getAssertions() || []} columns={columnDefinitions} structure={tableStructure} />
          </div>
        </div>
      )}

      <div className={`${baseClass}__logout-group`}>
        <LogoutButton />
        {myNutanixRedirectData && (
          <LogoutButton
            data-test="authorization-error__redirect-button"
            text={myNutanixRedirectData.label}
            className="mt-xs"
            customLogoutUrl={myNutanixRedirectData.url}
          />
        )}
      </div>
      <img className={`${baseClass}__logo`} src={Icons.NUTANIX_TYPE_LIGHT} alt="Nutanix" />
    </EmptyView>
  );
};

export default AuthorizationError;
